import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Upload, Clock, Download, Check, Star, ChevronRight, Send } from "lucide-react";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import HomepageROICalculator from "../components/HomepageROICalculator";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function QuickEnquiryForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "virtual_staging", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service_type: form.service,
        message: form.message || `Enquiry about ${form.service.replace("_", " ")}`,
      });
      setSubmitted(true);
      toast.success("Enquiry sent! We'll be in touch within 24 hours.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div data-testid="enquiry-success" className="bg-white border border-[#E8E8E4] p-8 text-center">
        <Check size={32} className="mx-auto mb-3 text-[#758C7B]" />
        <h3 className="font-['Outfit'] text-lg font-medium text-[#0D2D54] mb-2">Enquiry Sent</h3>
        <p className="text-sm text-[#6B7280]">We'll get back to you within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="quick-enquiry-form" className="bg-white border border-[#E8E8E4] p-6 space-y-4">
      <h3 className="font-['Outfit'] text-lg font-medium text-[#0D2D54]">Quick Enquiry</h3>
      <input
        type="text"
        data-testid="quick-name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="Your name *"
        className="w-full border border-[#E8E8E4] bg-white px-3 py-2.5 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none transition-colors"
      />
      <input
        type="email"
        data-testid="quick-email"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        placeholder="Email address *"
        className="w-full border border-[#E8E8E4] bg-white px-3 py-2.5 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none transition-colors"
      />
      <input
        type="tel"
        data-testid="quick-phone"
        value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })}
        placeholder="Phone (optional)"
        className="w-full border border-[#E8E8E4] bg-white px-3 py-2.5 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none transition-colors"
      />
      <select
        data-testid="quick-service"
        value={form.service}
        onChange={(e) => setForm({ ...form, service: e.target.value })}
        className="w-full border border-[#E8E8E4] bg-white px-3 py-2.5 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none transition-colors"
      >
        <option value="virtual_staging">Virtual Staging</option>
        <option value="3d_renders">3D Renders</option>
        <option value="general">General Enquiry</option>
      </select>
      <textarea
        data-testid="quick-message"
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        rows={3}
        placeholder="Tell us about your project (optional)"
        className="w-full border border-[#E8E8E4] bg-white px-3 py-2.5 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none transition-colors resize-none"
      />
      <button
        type="submit"
        data-testid="quick-submit"
        disabled={submitting}
        className="w-full bg-[#0D2D54] text-white py-3 text-sm font-medium tracking-wide hover:bg-[#1A3D6B] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {submitting ? "Sending..." : <><Send size={14} /> Send Enquiry</>}
      </button>
    </form>
  );
}

// Real separate before/after pairs for interactive slider
const SLIDER_PAIRS = [
  {
    before: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/q4flx958_%C2%A9Km1423-015.jpg",
    after: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/fcnds3p8_a7ce9d30c2bc32c8e70a95c518d24bd989bcff1f.jpg",
    room: "Hallway",
  },
  {
    before: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/vqhk056f_%C2%A9Km1423-019.jpg",
    after: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/u99fxqbe_11f9609129a58ed205c4686e53177911ec46c9dc.jpg",
    room: "Living Room",
  },
  {
    before: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/x98sf6b1_%C2%A9Km1423-046.jpg",
    after: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/nknrtc16_8a51d9414313ba1b8e7027f0d81b1fdc1ac5df85.jpg",
    room: "Bedroom",
  },
  {
    before: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/9g35xvcz_120%20kitchen%20.jpg",
    after: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/209l1yll_Manchester%20Road%20kitchen%201.jpg",
    room: "Kitchen",
  },
  {
    before: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/rtja0l53_image0.JPG",
    after: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/5zh8u2dd_8600836-interior02.JPEG",
    room: "Lounge",
  },
];

// Real before/after composite images from The Secret Seller portfolio
const BA_IMAGES = [
  {
    src: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/th4nhy8k_art%20deco%20ba.png",
    room: "Dining Room",
    style: "Art Deco",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/qqd1sss0_art%20deco%20bedroom%20ba.png",
    room: "Bedroom",
    style: "Art Deco",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/8pugvbul_art%20deco%20kitchen%20ba.png",
    room: "Kitchen & Living",
    style: "Art Deco",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/ryrvkvow_art%20deco%20lounge%20ba.png",
    room: "Lounge",
    style: "Art Deco",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/mqoejv7w_art%20deco%20snug%20ba.png",
    room: "Snug",
    style: "Art Deco",
  },
];

const TESTIMONIALS = [
  {
    name: "RW",
    role: "Estate Agent, Savills",
    text: "The Secret Seller has transformed our empty listings into sale-ready homes. We've seen a noticeable increase in enquiries since using virtual staging.",
    rating: 5,
  },
  {
    name: "Sarah Mitchell",
    role: "Property Developer",
    text: "Incredible turnaround time. We received our staged images within 48 hours and the quality was outstanding. A game-changer for off-plan marketing.",
    rating: 5,
  },
  {
    name: "JR",
    role: "Property Developer",
    text: "What a dream this team are to work with. Great ideas, fast turnaround time and they're always going above and beyond to make sure the finished scheme is exactly on brief. Highly recommended.",
    rating: 5,
  },
];

const SERVICES = [
  {
    title: "Virtual Staging",
    description: "Turn empty rooms into sale-ready spaces",
    badge: "Most Popular",
    image: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/fiv5p37c_Study%20-%20use%20one%20of%20these%20%281%29.jpg",
    cta: "Learn More",
    ctaLink: "/virtual-staging-uk",
    highlight: true,
  },
  {
    title: "3D Renders",
    description: "Ideal for off-plan and developments",
    image: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/mlpg0mca_stockton-roadexteriorfinal.jpg",
    cta: "Request Quote",
    ctaLink: "/3d-property-rendering-uk",
    highlight: false,
  },
];

export default function HomePage() {
  const [activeSlider, setActiveSlider] = useState(0);

  return (
    <main data-testid="home-page">
      {/* ===== HERO SECTION ===== */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/mfir6td4_Manchester%20Road%20kitchen%201.jpg"
            alt="Virtually staged kitchen by The Secret Seller"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0D2D54]/70 via-[#0D2D54]/35 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-8 lg:px-16 py-32 lg:py-0">
          <div className="max-w-2xl">
            <p className="uppercase text-[13px] tracking-[0.35em] text-white mb-8 font-normal opacity-0 animate-fade-in-up stagger-1">
              Premium Virtual Staging and Property Visualisation
            </p>
            <h1
              data-testid="hero-headline"
              className="font-['Outfit'] text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-[4rem] font-extralight text-white leading-[1.08] tracking-[-0.01em] mb-8 opacity-0 animate-fade-in-up stagger-2"
            >
              Transform empty properties into beautifully marketable homes
            </h1>
            <p className="text-base lg:text-[17px] text-white/65 leading-[1.7] mb-12 max-w-md opacity-0 animate-fade-in-up stagger-3">
              Realistic virtual staging and CGI renders designed to help listings stand out and attract buyers faster.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-up stagger-4">
              <Link
                to="/order"
                data-testid="hero-order-cta"
                className="inline-flex items-center justify-center gap-3 bg-white text-[#0D2D54] px-10 py-4 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-white/90 transition-all duration-300"
              >
                Send Us Your Photos
                <ArrowRight size={15} />
              </Link>
              <a
                href="#before-after"
                data-testid="hero-before-after-cta"
                className="inline-flex items-center justify-center gap-3 border border-white/25 text-white/90 px-10 py-4 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-white/8 transition-all duration-300"
              >
                View Our Work
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TRUST BAR ===== */}
      <section data-testid="trust-bar" className="bg-[#0D2D54] py-5">
        <div className="max-w-7xl mx-auto px-8 lg:px-16">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-white/80 text-[12px] tracking-wide">
            <span className="flex items-center gap-2"><Check size={14} className="text-[#758C7B]" /> From £55 per image</span>
            <span className="flex items-center gap-2"><Check size={14} className="text-[#758C7B]" /> Delivered in as little as 48 hours</span>
            <span className="flex items-center gap-2"><Check size={14} className="text-[#758C7B]" /> UK-wide service</span>
            <span className="flex items-center gap-2"><Check size={14} className="text-[#758C7B]" /> Virtual Staging and 3D Renders</span>
            <span className="flex items-center gap-2"><Check size={14} className="text-[#758C7B]" /> Trusted by estate agents, developers and sellers</span>
          </div>
        </div>
      </section>

      {/* ===== WHY IT WORKS ===== */}
      <section data-testid="why-it-works" className="py-28 lg:py-36">
        <div className="max-w-3xl mx-auto px-8 lg:px-16 text-center">
          <p className="uppercase text-[11px] tracking-[0.3em] text-[#758C7B] mb-4 font-medium">
            The Problem
          </p>
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-[2.5rem] tracking-[-0.01em] text-[#0D2D54] font-extralight mb-8">
            Why do empty properties struggle to sell?
          </h2>
          <p className="text-[15px] text-[#6B7280] leading-[1.8] mb-6">
            <strong className="text-[#0D2D54]">Buyers buy potential.</strong>
          </p>
          <p className="text-[15px] text-[#6B7280] leading-[1.8]">
            Empty rooms can feel cold, smaller than they are and difficult to visualise. Virtual staging and 3D renders help buyers imagine how a property could look, feel and function — creating stronger first impressions from the moment a listing goes live.
          </p>
        </div>
      </section>

      {/* ===== ROI CALCULATOR ===== */}
      <HomepageROICalculator />

      {/* ===== BEFORE & AFTER SECTION ===== */}
      <section id="before-after" data-testid="before-after-section" className="py-28 lg:py-36 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-8 lg:px-16">
          <div className="text-center mb-16 lg:mb-20">
            <p className="uppercase text-[11px] tracking-[0.3em] text-[#758C7B] mb-4 font-medium">
              View Our Work
            </p>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-[2.5rem] tracking-[-0.01em] text-[#0D2D54] font-extralight">
              See the difference
            </h2>
          </div>

          {/* Portfolio grid - before/after pairs */}
          <div className="space-y-16">
            {[
              {
                room: "Kitchen Family Room",
                before: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/x1mj9pi4_Kitchen-Family.jpg",
                after: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/lf6po1gv_Kitchen-Family.jpg",
              },
              {
                room: "Hallway",
                before: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/k1gb5lt8_Hall%20-%20use%20one%20of%20these%20%282%29.jpg",
                after: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/nsf4gjdn_Hall%20-%20use%20one%20of%20these%20%282%29.JPG",
              },
              {
                room: "Kitchen",
                before: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/ffv699ba_Kitchen.jpg",
                after: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/5cww2j5p_Kitchen.jpg",
              },
              {
                room: "Dining Room",
                before: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/wdv3db0n_Dining.jpg",
                after: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/ajn30wl2_Dining%20copy.JPG",
              },
              {
                room: "Home Office",
                before: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/t1o7w4wj_Study%20-%20use%20one%20of%20these%20%281%29.jpg",
                after: "https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/rw5yenl5_Study%20-%20use%20one%20of%20these%20%281%29.jpg",
              },
            ].map((pair, i) => (
              <div key={i} data-testid={`portfolio-pair-${i}`}>
                <p className="uppercase text-[11px] tracking-[0.25em] text-[#758C7B] mb-4 font-medium">{pair.room}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                  <div className="relative group overflow-hidden">
                    <img src={pair.before} alt={`${pair.room} before virtual staging`} className="w-full h-auto" loading="lazy" />
                    <span className="absolute bottom-3 left-3 bg-[#0D2D54]/80 text-white text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">Before</span>
                  </div>
                  <div className="relative group overflow-hidden">
                    <img src={pair.after} alt={`${pair.room} after virtual staging by The Secret Seller`} className="w-full h-auto" loading="lazy" />
                    <span className="absolute bottom-3 left-3 bg-[#758C7B]/90 text-white text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">After</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link
              to="/order"
              className="inline-flex items-center gap-3 bg-[#0D2D54] text-white px-8 py-3.5 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-[#1A3D6B] transition-all"
            >
              Send Us Your Photos
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== CASE STUDIES ===== */}
      <section data-testid="case-studies" className="py-28 lg:py-36">
        <div className="max-w-6xl mx-auto px-8 lg:px-16">
          <div className="text-center mb-16 lg:mb-20">
            <p className="uppercase text-[11px] tracking-[0.3em] text-[#758C7B] mb-4 font-medium">
              Case Studies
            </p>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-[2.5rem] tracking-[-0.01em] text-[#0D2D54] font-extralight">
              Real results
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {/* Case Study 1 */}
            <div data-testid="case-study-1" className="group relative overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/lf6po1gv_Kitchen-Family.jpg" alt="Virtually staged kitchen family room" className="w-full aspect-[4/3] object-cover" loading="lazy" />
              <div className="absolute top-4 left-4 bg-[#0D2D54] text-white px-4 py-2">
                <p className="text-[13px] font-medium leading-tight">Under offer</p>
                <p className="text-[18px] font-['Outfit'] font-light leading-tight">in 36 hours</p>
              </div>
              <div className="mt-4">
                <p className="text-sm text-[#0D2D54] font-medium">New build, North East</p>
                <p className="text-xs text-[#6B7280] mt-1">Virtual staging — 5 rooms</p>
              </div>
            </div>

            {/* Case Study 2 */}
            <div data-testid="case-study-2" className="group relative overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/nsf4gjdn_Hall%20-%20use%20one%20of%20these%20%282%29.JPG" alt="Virtually staged hallway" className="w-full aspect-[4/3] object-cover" loading="lazy" />
              <div className="absolute top-4 left-4 bg-[#758C7B] text-white px-4 py-2">
                <p className="text-[13px] font-medium leading-tight">Multiple offers</p>
                <p className="text-[18px] font-['Outfit'] font-light leading-tight">within the first week</p>
              </div>
              <div className="mt-4">
                <p className="text-sm text-[#0D2D54] font-medium">Development, North West — 24 plots</p>
                <p className="text-xs text-[#6B7280] mt-1">Virtual staging + 3D renders</p>
              </div>
            </div>

            {/* Case Study 3 */}
            <div data-testid="case-study-3" className="group relative overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/rw5yenl5_Study%20-%20use%20one%20of%20these%20%281%29.jpg" alt="Virtually staged home office" className="w-full aspect-[4/3] object-cover" loading="lazy" />
              <div className="absolute top-4 left-4 bg-[#0D2D54] text-white px-4 py-2">
                <p className="text-[13px] font-medium leading-tight">Sold for more</p>
                <p className="text-[18px] font-['Outfit'] font-light leading-tight">than asking price</p>
              </div>
              <div className="mt-4">
                <p className="text-sm text-[#0D2D54] font-medium">Bespoke Build, Yorkshire</p>
                <p className="text-xs text-[#6B7280] mt-1">Virtual staging — 4 rooms</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-14">
            <Link
              to="/order"
              data-testid="case-studies-cta"
              className="inline-flex items-center gap-3 bg-[#0D2D54] text-white px-8 py-3.5 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-[#1A3D6B] transition-all"
            >
              Send Us Your Photos
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" data-testid="how-it-works-section" className="py-28 lg:py-36">
        <div className="max-w-7xl mx-auto px-8 lg:px-16">
          <div className="text-center mb-16 lg:mb-20">
            <p className="uppercase text-[11px] tracking-[0.3em] text-[#758C7B] mb-4 font-medium">
              How It Works
            </p>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-[2.5rem] tracking-[-0.01em] text-[#0D2D54] font-extralight">
              Three simple steps
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-20 max-w-5xl mx-auto">
            {[
              { icon: Upload, step: "01", title: "Upload your photo", desc: "Upload images of your empty rooms directly through our platform" },
              { icon: Clock, step: "02", title: "We stage it within 48 hours", desc: "Our expert designers transform your space with professional virtual staging" },
              { icon: Download, step: "03", title: "Download and use instantly", desc: "Receive your staged images ready for listings and marketing" },
            ].map((item, i) => (
              <div key={i} data-testid={`how-it-works-step-${i + 1}`} className="text-center group">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FAFAF8] border border-[#E8E8E4] mb-6 group-hover:border-[#758C7B] group-hover:bg-[#758C7B]/10 transition-all duration-300">
                  <item.icon size={24} className="text-[#0D2D54]" />
                </div>
                <p className="text-[#758C7B] text-xs tracking-[0.2em] uppercase font-semibold mb-2">
                  Step {item.step}
                </p>
                <h3 className="font-['Outfit'] text-xl font-medium text-[#0D2D54] mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-[#6B7280] leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SERVICES SECTION ===== */}
      <section data-testid="services-section" className="py-20 lg:py-28 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12 lg:mb-16">
            <p className="uppercase text-xs tracking-[0.2em] text-[#758C7B] mb-3 font-semibold">
              What We Offer
            </p>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#0D2D54]">
              Our Services
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {SERVICES.map((service, i) => (
              <div
                key={i}
                data-testid={`service-card-${i}`}
                className={`bg-white border ${
                  service.highlight ? "border-[#0D2D54]" : "border-[#E8E8E4]"
                } overflow-hidden group hover:-translate-y-1 hover:shadow-xl transition-all duration-300`}
              >
                <div className="relative overflow-hidden h-56">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  {service.badge && (
                    <span className="absolute top-4 left-4 bg-[#758C7B] text-[#0D2D54] text-xs uppercase tracking-[0.15em] px-3 py-1.5 font-semibold">
                      {service.badge}
                    </span>
                  )}
                </div>
                <div className="p-8">
                  <h3 className="font-['Outfit'] text-xl font-medium text-[#0D2D54] mb-2">
                    {service.title}
                  </h3>
                  <p className="text-sm text-[#6B7280] mb-6">{service.description}</p>
                  <Link
                    to={service.ctaLink}
                    data-testid={`service-cta-${i}`}
                    className={`inline-flex items-center gap-2 text-sm font-medium tracking-wide transition-colors ${
                      service.highlight
                        ? "bg-[#0D2D54] text-white px-6 py-3 hover:bg-[#1A3D6B]"
                        : "text-[#0D2D54] hover:text-[#1A3D6B]"
                    }`}
                  >
                    {service.cta}
                    <ChevronRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRICING SECTION ===== */}
      <section id="pricing" data-testid="pricing-section" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12 lg:mb-16">
            <p className="uppercase text-xs tracking-[0.2em] text-[#758C7B] mb-3 font-semibold">
              Transparent Pricing
            </p>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#0D2D54]">
              Simple, transparent pricing
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Virtual Staging */}
            <div
              data-testid="pricing-virtual-staging"
              className="bg-white border-2 border-[#0D2D54] p-8 relative"
            >
              <span className="absolute -top-3 left-8 bg-[#758C7B] text-[#0D2D54] text-xs uppercase tracking-[0.15em] px-3 py-1 font-semibold">
                Most Popular
              </span>
              <h3 className="font-['Outfit'] text-xl font-medium text-[#0D2D54] mb-2">Virtual Staging</h3>
              <div className="mb-6">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-['Outfit'] text-4xl font-light text-[#0D2D54]">&pound;65</span>
                  <span className="text-sm text-[#6B7280]">per image</span>
                </div>
                <p className="text-sm text-[#758C7B] font-semibold">&pound;55 per image (10+ images)</p>
              </div>
              <ul className="space-y-3 mb-8">
                {["Delivered in 48 hours", "No subscription", "Only pay for what you need"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#6B7280]">
                    <Check size={16} className="text-[#758C7B] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/order"
                data-testid="pricing-staging-cta"
                className="block text-center bg-[#0D2D54] text-white px-6 py-3.5 text-sm font-medium tracking-wide hover:bg-[#1A3D6B] transition-all"
              >
                Send us your photos
              </Link>
              <Link
                to="/virtual-staging-uk"
                className="block text-center text-xs text-[#6B7280] mt-3 hover:text-[#0D2D54] transition-colors"
              >
                Learn more about virtual staging
              </Link>
            </div>
            <div data-testid="pricing-3d-renders" className="bg-white border border-[#E8E8E4] p-8">
              <h3 className="font-['Outfit'] text-xl font-medium text-[#0D2D54] mb-2">3D Renders</h3>
              <div className="mb-6">
                <span className="font-['Outfit'] text-2xl font-light text-[#0D2D54]">Bespoke pricing</span>
              </div>
              <p className="text-sm text-[#6B7280] mb-8 leading-relaxed">
                Premium architectural visualisations tailored to your project specifications.
              </p>
              <Link
                to="/3d-property-rendering-uk"
                data-testid="pricing-renders-cta"
                className="block text-center border border-[#0D2D54] text-[#0D2D54] px-6 py-3.5 text-sm font-medium tracking-wide hover:bg-[#0D2D54] hover:text-white transition-all"
              >
                Request a quote
              </Link>
            </div>
          </div>

          {/* Value Comparison */}
          <div data-testid="value-comparison" className="mt-12 text-center bg-[#FAFAF8] p-8 border border-[#E8E8E4]">
            <p className="font-['Outfit'] text-lg lg:text-xl text-[#0D2D54]">
              From <span className="text-[#758C7B] font-semibold">&pound;55 per image</span> vs{" "}
              <span className="line-through text-[#6B7280]">&pound;2,000+</span> for physical staging
            </p>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section data-testid="testimonials-section" className="py-20 lg:py-28 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12 lg:mb-16">
            <p className="uppercase text-xs tracking-[0.2em] text-[#758C7B] mb-3 font-semibold">
              Client Results
            </p>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#0D2D54]">
              What Our Clients Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="bg-white border border-[#E8E8E4] p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} size={14} className="fill-[#758C7B] text-[#758C7B]" />
                  ))}
                </div>
                <p className="text-sm text-[#6B7280] leading-relaxed mb-6 italic">
                  "{t.text}"
                </p>
                <div>
                  <p className="text-sm font-medium text-[#0D2D54]">{t.name}</p>
                  <p className="text-xs text-[#6B7280]">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TRUST SECTION ===== */}
      <section data-testid="trust-section" className="py-16 lg:py-20 border-t border-[#E8E8E4]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-sm text-[#6B7280] mb-10">
            Trusted by leading estate agents and developers across the UK
          </p>
          <div className="flex items-center justify-center gap-10 lg:gap-16 flex-wrap">
            <div data-testid="trust-logo-0" className="opacity-70 hover:opacity-100 transition-opacity duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/9jbcv9u8_savills-square.svg"
                alt="Savills estate agents - virtual staging client"
                className="h-10 lg:h-12 w-auto object-contain"
              />
            </div>
            <div data-testid="trust-logo-1" className="opacity-70 hover:opacity-100 transition-opacity duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/5lug3z5v_joneshomes.png"
                alt="Jones Homes - property developer client"
                className="h-10 lg:h-12 w-auto object-contain"
              />
            </div>
            <div data-testid="trust-logo-2" className="opacity-70 hover:opacity-100 transition-opacity duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/ajtw0a03_TSS%20logo%20update%20transparentlight.png"
                alt="The Secret Seller logo"
                className="h-10 lg:h-12 w-auto object-contain rounded-full"
              />
            </div>
            <div data-testid="trust-logo-3" className="opacity-70 hover:opacity-100 transition-opacity duration-300 flex items-center gap-2 border border-[#E8E8E4] px-4 py-2">
              <span className="font-['Outfit'] text-sm lg:text-base font-medium text-[#0D2D54] tracking-tight">
                Private Property Developers
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ SECTION (SEO + Featured Snippets) ===== */}
      <section id="faq" data-testid="faq-section" className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="uppercase text-xs tracking-[0.2em] text-[#758C7B] mb-3 font-semibold">
              Common Questions
            </p>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#0D2D54]">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-4">
            {[
              {
                q: "What is virtual staging and how does it work?",
                a: "Virtual staging is the process of digitally furnishing and decorating photos of empty rooms. You upload your property photos, our professional designers add furniture, decor and styling, and you receive photorealistic staged images within 48 hours. It's the fastest, most affordable way to make your listings stand out on Rightmove, Zoopla and other property portals."
              },
              {
                q: "How much does virtual staging cost in the UK?",
                a: "Our virtual staging starts at £65 per image, with a bulk discount of £55 per image when you order 10 or more. This is a fraction of the cost of physical staging, which typically costs £2,000+ per property. There's no subscription — you only pay for what you need."
              },
              {
                q: "How long does virtual staging take?",
                a: "We deliver all virtually staged images within 48 hours of receiving your photos. For urgent requests, please contact us at rachel@thesecretseller.co.uk and we'll do our best to accommodate faster turnaround times."
              },
              {
                q: "What types of rooms can you virtually stage?",
                a: "We can virtually stage any room type including living rooms, bedrooms, kitchens, dining rooms, bathrooms, home offices, hallways, conservatories and lofts. We offer a range of interior styles including modern, luxury, neutral, family, Scandinavian, contemporary, classic, industrial and minimalist."
              },
              {
                q: "Do you offer 3D property renders for developers?",
                a: "Yes, we provide photorealistic 3D renders for property developers, including both exterior and interior architectural visualisations. These are ideal for off-plan marketing, planning applications and sales brochures. Pricing is bespoke — contact us for a tailored quote."
              },
              {
                q: "Which estate agents use The Secret Seller?",
                a: "We work with leading UK estate agents including Savills, Jones Homes and hundreds of independent agents across England, Scotland and Wales. Our virtual staging and 3D renders are trusted by property professionals nationwide."
              },
            ].map((faq, i) => (
              <details
                key={i}
                data-testid={`faq-${i}`}
                className="group border border-[#E8E8E4] hover:border-[#758C7B]/50 transition-colors"
              >
                <summary className="flex items-center justify-between cursor-pointer p-5 text-sm font-medium text-[#0D2D54] list-none">
                  <span className="pr-4">{faq.q}</span>
                  <ChevronRight size={16} className="text-[#6B7280] group-open:rotate-90 transition-transform flex-shrink-0" />
                </summary>
                <div className="px-5 pb-5 text-sm text-[#6B7280] leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===== QUICK ENQUIRY FORM (Lead Capture) ===== */}
      <section data-testid="enquiry-section" className="py-20 lg:py-28 bg-[#FAFAF8]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="uppercase text-xs tracking-[0.2em] text-[#758C7B] mb-3 font-semibold">
                Get In Touch
              </p>
              <h2 className="font-['Outfit'] text-2xl sm:text-3xl tracking-tight text-[#0D2D54] mb-4">
                Not sure where to start?
              </h2>
              <p className="text-sm text-[#6B7280] leading-relaxed mb-6">
                Whether you're an estate agent, developer or landlord — we'll help you choose the right service for your property. No obligation, no hard sell.
              </p>
              <ul className="space-y-3 text-sm text-[#6B7280]">
                <li className="flex items-center gap-2">
                  <Check size={14} className="text-[#758C7B] flex-shrink-0" />
                  Free advice on which service suits your property
                </li>
                <li className="flex items-center gap-2">
                  <Check size={14} className="text-[#758C7B] flex-shrink-0" />
                  Response within 24 hours
                </li>
                <li className="flex items-center gap-2">
                  <Check size={14} className="text-[#758C7B] flex-shrink-0" />
                  No obligation quote
                </li>
              </ul>
              <p className="mt-6 text-sm text-[#6B7280]">
                Or email us directly:{" "}
                <a href="mailto:rachel@thesecretseller.co.uk" className="text-[#0D2D54] font-medium hover:underline">
                  rachel@thesecretseller.co.uk
                </a>
              </p>
            </div>
            <QuickEnquiryForm />
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section data-testid="final-cta-section" className="py-28 lg:py-36 bg-[#0D2D54]">
        <div className="max-w-3xl mx-auto px-8 lg:px-16 text-center">
          <p className="uppercase text-[11px] tracking-[0.3em] text-[#758C7B] mb-6 font-medium">
            Get Started
          </p>
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-[2.5rem] tracking-[-0.01em] text-white mb-6 font-extralight">
            Ready to transform your listing?
          </h2>
          <p className="text-[15px] text-white/50 mb-12 max-w-md mx-auto leading-relaxed">
            Join hundreds of UK estate agents who trust The Secret Seller for premium property visualisation.
          </p>
          <Link
            to="/order"
            data-testid="final-cta-btn"
            className="inline-flex items-center gap-2 bg-white text-[#0D2D54] px-10 py-4 text-sm font-semibold tracking-wide hover:bg-white/90 transition-all duration-300"
          >
            Send us your photos
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
